import { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Plan } from '@/hooks/usePlan';
import { cn } from '@/lib/cn';

export interface PlanLockedCardProps {
  required: Exclude<Plan, 'free'>;
  /** Override the localised title (defaults to `plan.locked_title`). */
  title?: ReactNode;
  /** Optional supporting copy under the title. */
  description?: ReactNode;
  /** Override the upgrade target. Defaults to `…/settings#billing`. */
  ctaTo?: string;
  className?: string;
}

const tierAccent: Record<PlanLockedCardProps['required'], string> = {
  growth: 'text-accent-700 bg-accent-50 ring-accent-200',
  global: 'text-indigo-700 bg-indigo-50 ring-indigo-200',
  enterprise: 'text-amber-700 bg-amber-50 ring-amber-200',
};

const tierButton: Record<PlanLockedCardProps['required'], string> = {
  growth: 'bg-accent-500 hover:bg-accent-600 text-white',
  global: 'bg-indigo-600 hover:bg-indigo-700 text-white',
  enterprise: 'bg-amber-500 hover:bg-amber-600 text-white',
};

const tierLabelKey: Record<PlanLockedCardProps['required'], string> = {
  growth: 'plan.tier_growth',
  global: 'plan.tier_global',
  enterprise: 'plan.tier_enterprise',
};

/**
 * Whole-section lock card. Replaces the gated page body with a friendly
 * "upgrade to unlock" panel instead of just hitting a 403.
 */
export function PlanLockedCard({
  required,
  title,
  description,
  ctaTo,
  className,
}: PlanLockedCardProps) {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const planLabel = t(tierLabelKey[required]);
  const target = ctaTo ?? (wsId ? `/app/workspace/${wsId}/settings#billing` : '#billing');

  return (
    <div
      className={cn(
        'mx-auto max-w-2xl rounded-2xl border border-ink-200 bg-white p-10 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full ring-1',
          tierAccent[required],
        )}
      >
        <Lock className="h-6 w-6" aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-ink-900">
        {title ?? t('plan.locked_title', { plan: planLabel })}
      </h2>
      <p className="mt-2 text-sm text-ink-600">
        {description ?? t('plan.locked_body', { plan: planLabel })}
      </p>
      <div className="mt-6">
        <Link
          to={target}
          className={cn(
            'inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition',
            tierButton[required],
          )}
        >
          {t('plan.upgrade_cta', { plan: planLabel })}
        </Link>
      </div>
    </div>
  );
}
